/**
 * Condition type detection utilities.
 * Store groups use "store-group-matching-rule" and retailers use "retailer-matching-rule".
 * Both the rule slug and ruleData structure are checked for maximum correctness.
 */

export const CONDITION_TYPE = {
  STORE: "store",
  RETAILER: "retailer",
  UNKNOWN: "unknown",
};

export const STORE_GROUP_MATCHING_RULE_SLUG = "store-group-matching-rule";
export const RETAILER_MATCHING_RULE_SLUG = "retailer-matching-rule";

/**
 * Determines the condition type based on ruleData structure.
 *
 * Detection Strategy:
 * - Store conditions have a `store_configuration` field (even if empty/null)
 * - Retailer conditions have `keywords` array but NO `store_configuration` field
 * - Unknown if neither pattern matches
 *
 * @param {object} ruleData - The rule's data payload
 * @returns {string} - CONDITION_TYPE.STORE, CONDITION_TYPE.RETAILER, or CONDITION_TYPE.UNKNOWN
 */
export const detectConditionType = (ruleData) => {
  if (!ruleData || typeof ruleData !== "object") {
    return CONDITION_TYPE.UNKNOWN;
  }

  // Store conditions have store_configuration field (even if null/empty)
  if ("store_configuration" in ruleData) {
    return CONDITION_TYPE.STORE;
  }

  // Retailer conditions have keywords but no store_configuration
  if (Array.isArray(ruleData.keywords) && ruleData.keywords.length > 0) {
    return CONDITION_TYPE.RETAILER;
  }

  return CONDITION_TYPE.UNKNOWN;
};

/**
 * Checks if a rule is a store condition.
 *
 * @param {object} rule - The outcome rule object
 * @returns {boolean}
 */
export const isStoreConditionRule = (rule) => {
  return (
    rule?.ruleDefinition?.slug === STORE_GROUP_MATCHING_RULE_SLUG &&
    detectConditionType(rule.ruleData) === CONDITION_TYPE.STORE
  );
};

/**
 * Checks if a rule is a retailer condition.
 *
 * @param {object} rule - The outcome rule object
 * @returns {boolean}
 */
export const isRetailerConditionRule = (rule) => {
  return (
    rule?.ruleDefinition?.slug === RETAILER_MATCHING_RULE_SLUG &&
    detectConditionType(rule.ruleData) === CONDITION_TYPE.RETAILER
  );
};

/**
 * Filters an array of rules to only store conditions.
 *
 * @param {Array} rules - Array of outcome rules
 * @returns {Array} - Filtered array of store condition rules
 */
export const filterStoreConditionRules = (rules) => {
  if (!Array.isArray(rules)) return [];
  return rules.filter(isStoreConditionRule);
};

/**
 * Filters an array of rules to only retailer conditions.
 *
 * @param {Array} rules - Array of outcome rules
 * @returns {Array} - Filtered array of retailer condition rules
 */
export const filterRetailerConditionRules = (rules) => {
  if (!Array.isArray(rules)) return [];
  return rules.filter(isRetailerConditionRule);
};
